import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

import styles from './Tabs.module.css';

interface TabsContextValue {
  baseId: string;
  value: string;
  select: (value: string) => void;
  orientation: 'horizontal' | 'vertical';
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabs = (component: string): TabsContextValue => {
  const context = useContext(TabsContext);
  if (context === null) throw new Error(`<${component}> must be rendered inside <Tabs>`);
  return context;
};

/** ids must survive canonical values like `act:S2.1`, which are not CSS-safe. */
const slug = (value: string): string => value.replace(/[^a-zA-Z0-9_-]/g, '-');

const tabId = (baseId: string, value: string): string => `${baseId}-tab-${slug(value)}`;
const panelId = (baseId: string, value: string): string => `${baseId}-panel-${slug(value)}`;

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled selection. */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  children: ReactNode;
}

export function Tabs({
  value,
  defaultValue,
  onChange,
  orientation = 'horizontal',
  className,
  children,
  ...rest
}: TabsProps) {
  const baseId = useId();
  const [internal, setInternal] = useState(defaultValue ?? '');
  const selected = value ?? internal;

  const select = useCallback(
    (next: string) => {
      if (value === undefined) setInternal(next);
      onChange?.(next);
    },
    [onChange, value],
  );

  return (
    <TabsContext.Provider value={{ baseId, value: selected, select, orientation }}>
      <div
        {...rest}
        className={clsx(styles.tabs, orientation === 'vertical' && styles.vertical, className)}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  /** Names the tab set. Required: a tablist with no name is unnavigable. */
  label: string;
  /** Contained tabs stretch to fill the row — the drill-down header style. */
  fill?: boolean;
  children: ReactNode;
}

/**
 * Roving tabindex, arrow-key navigation with automatic activation (APG "Tabs
 * with Automatic Activation"). Tabs are read from the DOM rather than from a
 * registry so composition — a tab inside a wrapper, a conditional tab — cannot
 * put the order out of sync with what the user sees.
 */
export const TabList = forwardRef<HTMLDivElement, TabListProps>(function TabList(
  { label, fill = false, className, children, onKeyDown, ...rest },
  ref,
) {
  const { orientation, select } = useTabs('TabList');

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    const tabs = [
      ...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'),
    ];
    if (tabs.length === 0) return;

    const current = tabs.findIndex((tab) => tab === document.activeElement);
    const forward = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    const back = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';

    let next = -1;
    if (event.key === forward) next = current < 0 ? 0 : (current + 1) % tabs.length;
    else if (event.key === back) next = current <= 0 ? tabs.length - 1 : current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    const target = tabs[next];
    if (target === undefined) return;
    event.preventDefault();
    target.focus();
    const value = target.dataset.value;
    if (value !== undefined) select(value);
  };

  return (
    <div
      {...rest}
      ref={ref}
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      className={clsx(styles.tabList, fill && styles.fill, className)}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
});

export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
  /** Right-aligned count, e.g. activities in a stage. */
  count?: number;
  icon?: ReactNode;
  children: ReactNode;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(
  { value, count, icon, className, children, onClick, ...rest },
  ref,
) {
  const { baseId, value: selectedValue, select } = useTabs('Tab');
  const selected = selectedValue === value;

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      role="tab"
      id={tabId(baseId, value)}
      data-value={value}
      aria-selected={selected}
      aria-controls={panelId(baseId, value)}
      tabIndex={selected ? 0 : -1}
      className={clsx(styles.tab, selected && styles.tabSelected, className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) select(value);
      }}
    >
      {icon !== undefined && (
        <span className={styles.tabIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles.tabLabel}>{children}</span>
      {count !== undefined && <span className={styles.tabCount}>{count}</span>}
    </button>
  );
});

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  /** Keep the panel mounted while hidden — use for expensive canvases. */
  keepMounted?: boolean;
  children: ReactNode;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(function TabPanel(
  { value, keepMounted = false, className, children, ...rest },
  ref,
) {
  const { baseId, value: selectedValue } = useTabs('TabPanel');
  const selected = selectedValue === value;

  if (!selected && !keepMounted) return null;

  return (
    <div
      {...rest}
      ref={ref}
      role="tabpanel"
      id={panelId(baseId, value)}
      aria-labelledby={tabId(baseId, value)}
      hidden={!selected}
      /* Focusable so a keyboard user can scroll the panel after Tab out of the
         tablist; APG's recommended treatment for panels without a first
         focusable child. */
      tabIndex={0}
      className={clsx(styles.tabPanel, className)}
    >
      {children}
    </div>
  );
});
